import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '../../services/AuthService/auth';
import { ExportRequestService } from '../../services/ExportRequestService/export-request';
import { AdminService } from '../../services/AdminService/admin';
import { ConfirmDialog } from '../../dialogs/confirm-dialog/confirm-dialog';
import * as perms from '../../shared/permissions';

const PENDING_COUNT_POLL_MS = 30000;

@Component({
  selector: 'app-shell',
  imports: [CommonModule, RouterLink, RouterLinkActive, RouterOutlet],
  templateUrl: './app-shell.html',
  styleUrl: './app-shell.scss',
})
export class AppShell implements OnInit, OnDestroy {
  session: ReturnType<AuthService['getSession']>;
  pendingExportRequests = signal(0);
  pendingCompanies = signal(0);

  private exportPollHandle?: ReturnType<typeof setInterval>;
  private companyPollHandle?: ReturnType<typeof setInterval>;

  constructor(
    private auth: AuthService,
    private router: Router,
    private exportRequestService: ExportRequestService,
    private adminService: AdminService,
    private dialog: MatDialog
  ) {
    this.session = this.auth.getSession();
  }

  get canReviewCompanies(): boolean {
    return perms.canReviewCompanies(this.session?.role);
  }

  get canManagePlatformUsers(): boolean {
    return perms.canManagePlatformUsers(this.session?.role);
  }

  get canManageProjects(): boolean {
    return perms.canManageProjects(this.session?.role);
  }

  get canViewProjects(): boolean {
    return perms.canViewProjects(this.session?.role);
  }

  get canManageApiKeys(): boolean {
    return perms.canManageApiKeys(this.session?.role);
  }

  get canManageCompanyUsers(): boolean {
    return perms.canManageCompanyUsers(this.session?.role);
  }

  get canViewOwnWallet(): boolean {
    return perms.canViewOwnWallet(this.session?.role);
  }

  get canViewPlatformWallet(): boolean {
    return perms.canViewPlatformWallet(this.session?.role);
  }

  get canViewPlatformConfig(): boolean {
    return perms.canViewPlatformConfig(this.session?.role);
  }

  get canViewOwnCompany(): boolean {
    return perms.canViewOwnCompany(this.session?.role);
  }

  get canViewCountrySchema(): boolean {
    return perms.canViewCountrySchema(this.session?.role);
  }

  get canViewPlatformDiagnostics(): boolean {
    return perms.canViewPlatformDiagnostics(this.session?.role);
  }

  get canViewPlatformExportRequests(): boolean {
    return perms.canViewPlatformExportRequests(this.session?.role);
  }

  ngOnInit(): void {
    if (this.canManageProjects) {
      this.loadPendingExportCount();
      this.exportPollHandle = setInterval(() => this.loadPendingExportCount(), PENDING_COUNT_POLL_MS);
    }

    if (this.canReviewCompanies) {
      this.loadPendingCompanyCount();
      this.companyPollHandle = setInterval(() => this.loadPendingCompanyCount(), PENDING_COUNT_POLL_MS);
    }
  }

  ngOnDestroy(): void {
    if (this.exportPollHandle) {
      clearInterval(this.exportPollHandle);
    }
    if (this.companyPollHandle) {
      clearInterval(this.companyPollHandle);
    }
  }

  private loadPendingExportCount(): void {
    this.exportRequestService.pendingCount().subscribe({
      next: (res) => this.pendingExportRequests.set(res.data.pending),
      error: () => {},
    });
  }

  private loadPendingCompanyCount(): void {
    this.adminService.pendingCompanyCount().subscribe({
      next: (res) => this.pendingCompanies.set(res.page?.count ?? res.data.length),
      error: () => {},
    });
  }

  openCompanyApprovals(): void {
    const status = this.pendingCompanies() > 0 ? 'PENDING' : '';
    this.router.navigate(['/admin/companies'], { queryParams: { status } });
  }

  logout(): void {
    this.dialog
      .open(ConfirmDialog, {
        width: '360px',
        data: {
          title: 'Log out',
          message: 'Are you sure you want to log out?',
          confirmLabel: 'Log out',
          danger: true,
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.auth.logout();
          this.router.navigate(['/login']);
        }
      });
  }
}
